import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Treatment } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import {
  useBodyAreasQuery,
  useDevicesQuery,
  useTreatmentCategoriesQuery,
  useTreatmentsQuery,
} from '@/hooks/useTreatmentLibraryQueries';
import { useSetTreatmentActiveMutation } from '@/hooks/useTreatmentLibraryMutations';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import {
  formatBilingualName,
  resolveLocalizedName,
  resolveOtherLanguageName,
} from './localizedName';
import { TreatmentFormModal } from './TreatmentFormModal';

interface TreatmentsPaneProps {
  onManageBodyAreas: () => void;
  onManageDevices: () => void;
}

export function TreatmentsPane({ onManageBodyAreas, onManageDevices }: TreatmentsPaneProps) {
  const { t } = useTranslation('treatmentLibrary');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Treatment | null>(null);

  const categoriesQuery = useTreatmentCategoriesQuery(true);
  const bodyAreasQuery = useBodyAreasQuery(true);
  const devicesQuery = useDevicesQuery(true);
  const treatmentsQuery = useTreatmentsQuery(
    debouncedSearch,
    categoryFilter === 'all' ? null : categoryFilter,
    true,
    true
  );
  const setActiveMutation = useSetTreatmentActiveMutation();

  const categories = categoriesQuery.data === undefined ? [] : categoriesQuery.data;
  const bodyAreas = bodyAreasQuery.data === undefined ? [] : bodyAreasQuery.data;
  const devices = devicesQuery.data === undefined ? [] : devicesQuery.data;

  function openAdd() {
    setEditing(null);
    setIsModalOpen(true);
  }
  function openEdit(treatment: Treatment) {
    setEditing(treatment);
    setIsModalOpen(true);
  }
  function toggleActive(treatment: Treatment) {
    setActiveMutation.mutate(
      { treatmentId: treatment.id, isActive: !treatment.isActive },
      {
        onSuccess: function done() {
          showSuccessNotification(
            treatment.isActive ? t('treatments.deactivated') : t('treatments.activated')
          );
        },
        onError: function fail(error) {
          showErrorNotification(resolveApiErrorMessage(error));
        },
      }
    );
  }

  return (
    <div>
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="flex-1 min-w-0 flex items-center gap-2.5 bg-zoowhite border border-zooborder rounded-xl px-3.5">
          <Icon name="Search" className="text-zoomuted" />
          <input
            type="search"
            value={searchInput}
            placeholder={t('treatments.searchPlaceholder')}
            onChange={function update(changeEvent) {
              setSearchInput(changeEvent.target.value);
            }}
            className="flex-1 min-w-0 bg-transparent border-none outline-none py-2.5 text-sm text-zooindigo"
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1 lg:w-56">
            <SearchableSelect
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={[
                { value: 'all', label: t('treatments.allCategories') },
                ...categories.map(function renderOption(category) {
                  return {
                    value: category.id,
                    label: resolveLocalizedName(category.nameEn, category.namePl),
                  };
                }),
              ]}
            />
          </div>
          <Button type="button" className="flex-shrink-0" onClick={openAdd}>
            <Icon name="Plus" /> {t('treatments.add')}
          </Button>
        </div>
      </div>

      {treatmentsQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : treatmentsQuery.isError === true || treatmentsQuery.data === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            treatmentsQuery.refetch();
          }}
        />
      ) : treatmentsQuery.data.length === 0 ? (
        <Card
          padding="lg"
          variant="dashed"
          shadow={false}
          className="text-center text-sm text-zoomuted"
        >
          {debouncedSearch.trim().length > 0 || categoryFilter !== 'all'
            ? t('treatments.noResults')
            : t('treatments.empty')}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {treatmentsQuery.data.map(function renderCard(treatment) {
            return (
              <TreatmentCard
                key={treatment.id}
                treatment={treatment}
                onEdit={openEdit}
                onToggleActive={toggleActive}
                isTogglingActive={
                  setActiveMutation.isPending &&
                  setActiveMutation.variables !== undefined &&
                  setActiveMutation.variables.treatmentId === treatment.id
                }
              />
            );
          })}
        </div>
      )}

      {isModalOpen ? (
        <TreatmentFormModal
          treatment={editing}
          categories={categories}
          bodyAreas={bodyAreas}
          devices={devices}
          onClose={function close() {
            setIsModalOpen(false);
          }}
          onManageBodyAreas={function manage() {
            setIsModalOpen(false);
            onManageBodyAreas();
          }}
          onManageDevices={function manage() {
            setIsModalOpen(false);
            onManageDevices();
          }}
        />
      ) : null}
    </div>
  );
}

interface TreatmentCardProps {
  treatment: Treatment;
  onEdit: (treatment: Treatment) => void;
  onToggleActive: (treatment: Treatment) => void;
  isTogglingActive: boolean;
}

function TreatmentCard({
  treatment,
  onEdit,
  onToggleActive,
  isTogglingActive,
}: TreatmentCardProps) {
  const { t } = useTranslation('treatmentLibrary');
  const deviceLine =
    treatment.device === null
      ? t('treatments.noDevice')
      : resolveLocalizedName(treatment.device.nameEn, treatment.device.namePl) +
        (treatment.device.settingFields.length > 0
          ? ' · ' +
            treatment.device.settingFields
              .map(function toName(field) {
                return field.name;
              })
              .join(' · ')
          : '');

  return (
    <Card
      as="article"
      padding="none"
      className={
        treatment.isActive ? 'p-4 flex flex-col gap-3' : 'p-4 flex flex-col gap-3 opacity-60'
      }
    >
      <div className="flex items-start gap-3">
        {treatment.imageUrl === null ? (
          <span
            className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
              treatment.category === null ? 'bg-zoovpalelavender text-zooprimary' : ''
            )}
            style={
              treatment.category === null
                ? undefined
                : {
                    backgroundColor: treatment.category.color + '22',
                    color: treatment.category.color,
                  }
            }
          >
            <Icon name="Activity" />
          </span>
        ) : (
          <img
            src={treatment.imageUrl}
            alt={resolveLocalizedName(treatment.nameEn, treatment.namePl)}
            className="w-10 h-10 rounded-xl object-cover border border-zooborder shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-bold text-zooindigo truncate">
            {resolveLocalizedName(treatment.nameEn, treatment.namePl)}
          </div>
          <div className="text-xs text-zoomuted truncate">
            {resolveOtherLanguageName(treatment.nameEn, treatment.namePl)}
          </div>
        </div>
        {treatment.category === null ? null : (
          <Badge
            uppercase
            className="px-2.5 py-1 whitespace-nowrap"
            style={{
              backgroundColor: treatment.category.color + '22',
              color: treatment.category.color,
            }}
          >
            {resolveLocalizedName(treatment.category.nameEn, treatment.category.namePl)}
          </Badge>
        )}
      </div>

      {treatment.bodyAreas.length === 0 ? null : (
        <div className="flex flex-wrap gap-1.5">
          {treatment.bodyAreas.map(function renderArea(area) {
            return (
              <span
                key={area.id}
                className="text-[11.5px] font-semibold text-zooindigo bg-zoooffwhite border border-zooborder rounded-lg px-2 py-0.5"
              >
                {formatBilingualName(area.nameEn, area.namePl)}
              </span>
            );
          })}
        </div>
      )}

      <div className="text-xs text-zoomuted flex items-start gap-1.5">
        <Icon name="Monitor" className="text-zooviolet shrink-0 mt-0.5" />
        <span>{deviceLine}</span>
      </div>

      {treatment.contraindication === null || treatment.contraindication.length === 0 ? null : (
        <div className="flex items-start gap-2 bg-zoowarningbg text-zoowarningtext rounded-lg px-2.5 py-2 text-xs">
          <Icon name="AlertCircle" className="shrink-0 mt-0.5" />
          {treatment.contraindication}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-zooborder pt-2.5 mt-0.5">
        <span className="text-xs text-zoomuted">
          {treatment.isActive
            ? treatment.usageCount > 0
              ? t('treatments.usage', {
                  uses: treatment.usageCount,
                  patients: treatment.patientCount,
                })
              : t('treatments.notUsed')
            : t('treatments.inactive')}
        </span>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('treatments.edit')}
            onClick={function edit() {
              onEdit(treatment);
            }}
          >
            <Icon name="Edit" />
          </Button>
          <ToggleSwitch
            isChecked={treatment.isActive}
            isPending={isTogglingActive}
            onToggle={function toggle() {
              onToggleActive(treatment);
            }}
            accessibleLabel={t('treatments.activeLabel')}
          />
        </div>
      </div>
    </Card>
  );
}
